// Video links of the website sections "Startbereich" and "Banner 2". A pasted
// YouTube or Wistia link becomes the embed URL that the customer website puts
// into an iframe. Only two URL shapes come out of here (see validateEmbedUrl).
import {
  WISTIA_MEDIA_ID_REGEX,
  YOUTUBE_VIDEO_ID_REGEX
} from "../components/strings";

const YOUTUBE_EMBED_BASE = "https://www.youtube.com/embed";
const WISTIA_EMBED_BASE = "https://fast.wistia.net/embed/iframe";
const WISTIA_OEMBED_URL = "https://fast.wistia.net/oembed";
export const WISTIA_OEMBED_TIMEOUT_MS = 5000;

const YOUTUBE_HOSTS = ["www.youtube.com", "youtube.com"];
const WISTIA_EMBED_HOSTS = ["fast.wistia.net", "fast.wistia.com"];
// Account pages, e.g. am-beratung.wistia.com
const WISTIA_ACCOUNT_HOST_REGEX = /^(?:[a-z0-9-]+\.)*wistia\.com$/;
const WISTIA_SHARE_PATH_REGEX = /^\/s\/[A-Za-z0-9_-]+\/?$/;

const parseUrl = (value) => {
  try {
    return new URL(String(value || "").trim());
  } catch {
    return null;
  }
};

const toYoutubeEmbedUrl = (url) => {
  let videoId;
  if (YOUTUBE_HOSTS.includes(url.hostname)) {
    if (url.pathname.startsWith("/embed/")) {
      videoId = url.pathname.split("/embed/")[1];
    } else if (url.pathname === "/watch") {
      videoId = url.searchParams.get("v");
    }
  } else if (url.hostname === "youtu.be") {
    videoId = url.pathname.slice(1);
  }
  return videoId && YOUTUBE_VIDEO_ID_REGEX.test(videoId)
    ? `${YOUTUBE_EMBED_BASE}/${videoId}`
    : null;
};

const toWistiaEmbedUrl = (url) => {
  if (url.protocol !== "https:") return null;
  let mediaId;
  if (WISTIA_EMBED_HOSTS.includes(url.hostname)) {
    mediaId = url.pathname.match(/^\/embed\/iframe\/([^/]+)\/?$/)?.[1];
  } else if (WISTIA_ACCOUNT_HOST_REGEX.test(url.hostname)) {
    mediaId = url.pathname.match(/^\/medias\/([^/]+)\/?$/)?.[1];
  }
  return mediaId && WISTIA_MEDIA_ID_REGEX.test(mediaId)
    ? `${WISTIA_EMBED_BASE}/${mediaId}`
    : null;
};

// Links that carry the video ID: YouTube, Wistia media and embed links
export const toEmbedUrlSync = (value) => {
  const url = parseUrl(value);
  if (!url) return null;
  return toYoutubeEmbedUrl(url) || toWistiaEmbedUrl(url);
};

// Wistia's "Share" dialog gives https://<account>.wistia.com/s/<token>; the
// token is not the media ID, only Wistia's oEmbed endpoint knows the video.
export const isWistiaShareLink = (value) => {
  const url = parseUrl(value);
  return Boolean(
    url &&
    url.protocol === "https:" &&
    WISTIA_ACCOUNT_HOST_REGEX.test(url.hostname) &&
    WISTIA_SHARE_PATH_REGEX.test(url.pathname)
  );
};

export const resolveWistiaShareLink = async (
  value,
  { fetchImpl = fetch, timeoutMs = WISTIA_OEMBED_TIMEOUT_MS } = {}
) => {
  if (!isWistiaShareLink(value)) return null;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetchImpl(
      `${WISTIA_OEMBED_URL}?url=${encodeURIComponent(String(value).trim())}`,
      { signal: controller.signal }
    );
    if (!response.ok) return null;
    const { html } = await response.json();
    const src = String(html || "").match(/<iframe[^>]*\ssrc="([^"]+)"/)?.[1];
    if (!src) return null;
    const url = parseUrl(src);
    // Only the player URL is taken from Wistia's answer
    return url && WISTIA_EMBED_HOSTS.includes(url.hostname)
      ? toWistiaEmbedUrl(url)
      : null;
  } catch (error) {
    console.log("Wistia oEmbed lookup failed: ", error);
    return null;
  } finally {
    clearTimeout(timer);
  }
};
